import React, { useState, useEffect } from 'react';
import { Divider, FormControlLabel, Grid, InputLabel, MenuItem, Radio, Typography, Button } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useStyles } from 'components/Reports';
import { Formik, Form, Field } from 'formik';
import { Select, RadioGroup, TextField } from 'formik-material-ui';
import { DateFormik } from 'components/Shared/CustomInputs';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { ReportTypes, ReportFormats } from 'enums';
import SchedulerDialog from './SchedulerDialog';
import Schema from 'schemas/ReportGenerator.schema';
import { buildYupSchema } from 'utils/SchemaUtils';
import { runReport } from 'utils/ReportUtils';
import { NotificationsSnackBar } from 'components/Shared/DynamicForm';
import reportdata from './ReportType.json'
import { getAllResources } from 'utils/ResourcesUtils';

const refreshLagTime = 2000;
const inputVariant = "standard";

// Matches the "value" of the weather entry in ReportType.json.
const weatherSummaryReport = "WeatherSummaryReport";

// Retention window the weather job purges against (Jobs/Weather.cs). Keep the two in sync.
const weatherRetentionDays = 90;

const initialValues = {
    ReportType: "WorkItemSummary",
    StartDate: null,
    EndDate: null,
    Format: "PDF",
    Recipients: []
};

const ReportGenerator = ({ selectedProject, systemUserId }) => {
    const classes = useStyles();
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

    const openScheduler = () => setScheduleDialogOpen(true);
    const closeScheduler = () => setScheduleDialogOpen(false);
    const [resources, setResources] = useState([]);
    const [reportType, setReportType] = useState([]);
    const validator = buildYupSchema(Schema);
    const [reload, setReload] = useState(true);


    useEffect(() => {
        if (resources.length === 0 && reload) {
            const getR = async () => {
                const Rs = await getAllResources(selectedProject)
                setReportType(reportdata.reporttype)
                setResources(Rs.data.sort((a, b) => (a.Name_SystemUser.toLowerCase() > b.Name_SystemUser.toLowerCase()) ? 1 : -1))
                setReload(false)
            };
            getR()
        }
    }, [resources])

    return (
        <Grid item >
            <Typography variant="h4" color="primary">Report Generation</Typography>
            <Divider />
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Formik
                    initialValues={initialValues}
                    validateOnMount={true}
                    validationSchema={validator}
                    onSubmit={async (values, { setSubmitting, setStatus }) => {
                        const editResult = await runReport(values, selectedProject, systemUserId);
                        if (editResult.success) {
                            setStatus({ success: false, message: "Form was submitted successfully", alertType: "success" });
                            await new Promise(resolve => setTimeout(resolve, refreshLagTime));
                        } else {
                            setStatus({ success: false, message: "There was an issue submitting the form", alertType: "error" });
                            await new Promise(resolve => setTimeout(resolve, refreshLagTime));
                        };

                        setSubmitting(false)
                    }}
                >
                    {({ values, isSubmitting, resetForm, status, setStatus, isValid }) => (
                        <Form>
                            <Grid container spacing={4} className={classes.generatorForm}>
                                <Grid item lg={3} md={3} sm={12}>
                                    <InputLabel htmlFor="ReportType">Report Type</InputLabel>
                                    <Field
                                        style={{ width: "100%" }}
                                        name="ReportType"
                                        variant={inputVariant}
                                        component={Select}
                                        inputProps={{
                                            id: "ReportType"
                                        }}
                                    >
                                      
                                        {reportType && reportType.map((item, index) =>
                                            (item.projects.includes(selectedProject) ||  item.projects.includes("*"))&&
                                            <MenuItem value={item.value} key={item.value}>{item.name}</MenuItem>
                                        )}

                                    </Field>
                                </Grid>
                                <Grid item lg={2} md={2} sm={6}>
                                    <InputLabel htmlFor="StartDate">Start Date</InputLabel>
                                    <Field
                                        style={{ width: "100%" }}
                                        name="StartDate"
                                        variant={inputVariant}
                                        component={DateFormik}
                                        inputProps={{
                                            id: "StartDate"
                                        }}
                                        format="MM/dd/yyyy"
                                        clearable
                                    >
                                    </Field>
                                </Grid>
                                <Grid item lg={2} md={2} sm={6}>
                                    <InputLabel htmlFor="EndDate">End Date</InputLabel>
                                    <Field
                                        style={{ width: "100%" }}
                                        name="EndDate"
                                        variant={inputVariant}
                                        component={DateFormik}
                                        inputProps={{
                                            id: "EndDate"
                                        }}
                                        format="MM/dd/yyyy"
                                        clearable
                                    >
                                    </Field>
                                </Grid>
                                <Grid item sm={12} xs={12} md={2} lg={2} xl={2}>
                                    <InputLabel htmlFor="">Select Format</InputLabel>
                                    <Field component={RadioGroup} name="Format">
                                        {
                                            Object.entries(ReportFormats).map(([value, description]) => (
                                                <FormControlLabel
                                                    value={value}
                                                    control={<Radio disabled={isSubmitting} />}
                                                    label={description}
                                                    disabled={isSubmitting}
                                                    key={value}
                                                />
                                            ))
                                        }
                                    </Field>
                                </Grid>
                                <Grid item sm={12} xs={12} md={2} lg={2} xl={2}>
                                    <InputLabel htmlFor="Recipients">
                                        Send Results To:
                                    </InputLabel>
                                    <Field
                                        style={{ width: "100%" }}
                                        component={TextField}
                                        type="text"
                                        name="Recipients"
                                        select
                                        SelectProps={{ multiple: true }}
                                        inputProps={{ name: 'Recipients', id: 'Recipients' }}
                                    >
                                        <MenuItem value="ashley.lincoln@kci.com">Ashley Lincoln</MenuItem>

                                        {resources && resources.map((item, index) =>
                                            <MenuItem value={item.Email_SystemUser}>{item.Name_SystemUser}</MenuItem>
                                        )}



                                    </Field>
                                </Grid>
                            </Grid>
                            <Grid container justify="flex-end" alignItems="center">
                                {values.ReportType === weatherSummaryReport &&
                                    <Grid item style={{ marginRight: "auto", paddingRight: 16 }}>
                                        <Alert severity="info">
                                            Please note only the last {weatherRetentionDays} days of weather are kept in the system.
                                        </Alert>
                                    </Grid>
                                }
                                <Grid item className={classes.runReportButtons}>
                                    <Button variant="contained" color="primary" type="submit">Run Report</Button>
                                </Grid>
                            </Grid>
                            <br />
                            <Grid container justify="flex-end">
                                <Grid item className={classes.runReportButtons}>
                                    <Button variant="contained" onClick={openScheduler}
                                        disabled={!isValid} >Create Schedule</Button>
                                </Grid>
                            </Grid>
                            <SchedulerDialog
                                open={scheduleDialogOpen}
                                close={closeScheduler}
                                formValues={values}
                                inputVariant={inputVariant}
                                resetForm={resetForm}
                                selectedProject={selectedProject}
                                systemUserId={systemUserId}
                            />
                            <NotificationsSnackBar status={status} setStatus={setStatus} refreshLagTime={refreshLagTime} />
                        </Form>
                    )}
                </Formik>
            </MuiPickersUtilsProvider>
        </Grid>
    )
};

export default ReportGenerator;